/**
 * A flavour wheel inside a development-chat answer (D16).
 *
 * The chat draws one when the team has a choice to make — two bases, two
 * modifiers, which note to push — so the choice can be seen rather than read.
 * The message carries only what to draw (`ChatWheel`: ingredients and the
 * notes to look at); the scores are read here, through the same calls and the
 * same `resolveFlavour` as the wheel screen, so a thread reopened next week
 * shows the bar's links and note edits as they are then.
 *
 * Tapping a note shows each ingredient's score for it. The buttons under the
 * wheel write an answer into the composer for the person to send or change;
 * nothing is sent for them.
 */

import { useEffect, useMemo, useState } from "react";
import {
  flavourNameKey,
  flavourRefsFor,
  ingredientColour,
  noteVerdict,
  resolveFlavour,
  FLAVOUR_NOTES,
  MAX_WHEEL_INGREDIENTS,
  type ChatWheel,
  type FlavourProfile,
  type Ingredient,
  type Spec,
  type WheelMode,
} from "@baros/core";
import { flavourApi } from "./flavour-api.js";
import { FlavourWheel } from "./FlavourWheel.js";
import "./chat-wheel.css";

interface Props {
  readonly wheel: ChatWheel;
  readonly token: string;
  readonly ingredients: ReadonlyMap<string, Ingredient>;
  readonly specs: ReadonlyMap<string, Spec>;
  /** Put text in the composer. Absent on a read-only view. */
  readonly onAnswer?: ((text: string) => void) | undefined;
}

export function ChatWheelCard(p: Props) {
  const items = useMemo(() => p.wheel.items.slice(0, MAX_WHEEL_INGREDIENTS), [p.wheel]);
  // Every flavour-database name the items need: a linked ingredient's own, a
  // batch's ingredients', or the name itself for something the bar lacks.
  const refs = useMemo(
    () => [
      ...new Set([
        ...flavourRefsFor(
          items.flatMap((i) => (i.ingredientId ? [i.ingredientId] : [])),
          p.ingredients,
          p.specs,
        ),
        ...items.flatMap((i) => (!i.ingredientId && i.ref ? [i.ref] : [])),
      ]),
    ],
    [items, p.ingredients, p.specs],
  );
  const refsKey = JSON.stringify(refs);

  const [profiles, setProfiles] = useState<ReadonlyMap<string, FlavourProfile> | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    setFailed(false);
    flavourApi
      .profiles(p.token, JSON.parse(refsKey) as string[])
      .then((found) => live && setProfiles(new Map(found.map((f) => [flavourNameKey(f.name), f]))))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [p.token, refsKey]);

  const [mode, setMode] = useState<WheelMode>("key");
  const [note, setNote] = useState<number | null>(p.wheel.focus[0] ?? null);

  const drawn = useMemo(() => {
    if (!profiles) return [];
    return items.flatMap((item, i) => {
      const profile = item.ingredientId
        ? resolveFlavour(item.ingredientId, p.ingredients, p.specs, profiles)?.profile
        : profiles.get(flavourNameKey(item.ref ?? ""));
      return profile
        ? [{ key: item.ingredientId ?? item.ref ?? item.label, label: item.label, colour: ingredientColour(i), profile }]
        : [];
    });
  }, [profiles, items, p.ingredients, p.specs]);

  if (failed) return <div className="chat-wheel muted">The flavour wheel could not be loaded.</div>;
  if (!profiles) return <div className="chat-wheel muted">Drawing the flavour wheel…</div>;
  if (drawn.length === 0) return <div className="chat-wheel muted">No flavour data for these ingredients yet.</div>;

  const info = note !== null ? FLAVOUR_NOTES[note] : undefined;

  return (
    <div className="chat-wheel">
      {p.wheel.question && <div className="chat-wheel-q">{p.wheel.question}</div>}
      <div className="chat-wheel-legend">
        {drawn.map((d) => (
          <span key={d.key} className="chat-wheel-key">
            <span className="dot" style={{ background: d.colour }} />
            {d.label}
          </span>
        ))}
        <span className="spacer" />
        <button type="button" className="btn sm" aria-pressed={mode === "key"} onClick={() => setMode("key")}>
          Key notes
        </button>
        <button type="button" className="btn sm" aria-pressed={mode === "all"} onClick={() => setMode("all")}>
          All notes
        </button>
      </div>

      <FlavourWheel items={drawn} mode={mode} selectedNote={note} onNote={setNote} compact />

      {p.wheel.focus.length > 0 && (
        <div className="chat-wheel-focus">
          <span className="label">Look at</span>
          {p.wheel.focus.map((n) => (
            <button key={n} type="button" className="btn sm" aria-pressed={note === n} onClick={() => setNote(n)}>
              {FLAVOUR_NOTES[n]?.label ?? n}
            </button>
          ))}
        </div>
      )}

      {info && (
        <div className="chat-wheel-note">
          <div className="label">
            {info.label} · {info.family}
          </div>
          {drawn.map((d) => {
            const v = d.profile.notes[info.index] ?? 0;
            return (
              <div key={d.key} className="chat-wheel-score">
                <span className="n">{d.label}</span>
                <span className="track">
                  <span style={{ width: `${v * 10}%`, background: d.colour }} />
                </span>
                <span className="num">{v}</span>
              </div>
            );
          })}
          <div className="muted">{noteVerdict(info.index, drawn.map((d) => ({ name: d.label, profile: d.profile })))}</div>
        </div>
      )}

      {p.onAnswer && (
        <div className="chat-wheel-answers">
          {drawn.length > 1 &&
            drawn.map((d) => (
              <button key={d.key} type="button" className="btn sm" onClick={() => p.onAnswer?.(`Let's go with ${d.label}.`)}>
                Go with {d.label}
              </button>
            ))}
          {info && (
            <button type="button" className="btn sm" onClick={() => p.onAnswer?.(`Lean into the ${info.label.toLowerCase()} note.`)}>
              Lean into {info.label.toLowerCase()}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
